import type { ActionFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Form, useActionData, useNavigation, Link } from "@remix-run/react";
import { useState, useEffect } from "react";
import "~/shared/components/dark-mode.css";
import "~/shared/components/animations.css";
import moment from "moment";
import "moment-hijri";

import { getServiceKeywords, createSEODescription } from "~/shared/utils/seo-keywords";

export const meta: MetaFunction = () => {
  const keywords = getServiceKeywords('dateDifference');
  const keywordsString = keywords.join(', ');
  const description = createSEODescription('dateDifference');
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "حاسبة الفرق بين التواريخ - أسامة الزبيدي | احسب الفرق بين أي تاريخين بدقة عالية | Date Difference Calculator - Osama Al-Zubaidi" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: description },
    
    // الكلمات المفتاحية المحسنة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    
    // Open Graph - محسن لـ SEO
    { property: "og:title", content: "حاسبة الفرق بين التواريخ - أسامة الزبيدي | احسب الفرق بين أي تاريخين بدقة عالية" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://oz.sa/date-difference" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "حاسبة الفرق بين التواريخ - أسامة الزبيدي" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "حاسبة الفرق بين التواريخ - أسامة الزبيدي | احسب الفرق بين أي تاريخين بدقة عالية" },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@osama_zubaidi" },
    { name: "twitter:site", content: "@osama_zubaidi" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "حاسبة الفرق بين التواريخ - أسامة الزبيدي" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa/date-difference" },
    { name: "alternate", content: "https://oz.sa/date-difference", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa/date-difference", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa/date-difference", "hreflang": "x-default" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
  ];
};

// Translations
const translations = {
  ar: {
    title: "حاسبة الفرق بين التواريخ",
    description: "احسب الفرق بين أي تاريخين بالتقويم الميلادي والهجري",
    selectCalendar: "اختر نظام التقويم:",
    enterFirstDate: "أدخل التاريخ الأول:",
    enterSecondDate: "أدخل التاريخ الثاني:",
    day: "اليوم",
    month: "الشهر",
    year: "السنة",
    calculate: "احسب الفرق",
    calculating: "جاري الحساب...",
    results: "النتائج",
    difference: "الفرق بين التاريخين",
    years: "سنوات",
    months: "أشهر",
    days: "أيام",
    weeks: "أسابيع",
    firstDate: "التاريخ الأول",
    secondDate: "التاريخ الثاني",
    correspondingHijri: "التاريخ الهجري الموافق",
    correspondingGregorian: "التاريخ الميلادي الموافق",
    totalDays: "إجمالي الأيام",
    totalWeeks: "إجمالي الأسابيع",
    totalMonths: "إجمالي الأشهر",
    totalYears: "إجمالي السنوات",
    exactDifference: "الفرق بالضبط",
    footer: {
      accuracy: "جميع الحسابات تعتمد على التقويم الهجري المعتمد (أم القرى)",
      calendar: "التقويم الهجري المعتمد هو تقويم أم القرى",
      copyright: "صنع بكل ❤️ في 🇸🇦"
    }
  },
  en: {
    title: "Date Difference Calculator",
    description: "Calculate the difference between any two dates in both Gregorian and Hijri calendars",
    selectCalendar: "Select Calendar System:",
    enterFirstDate: "Enter First Date:",
    enterSecondDate: "Enter Second Date:",
    day: "Day",
    month: "Month",
    year: "Year",
    calculate: "Calculate Difference",
    calculating: "Calculating...",
    results: "Results",
    difference: "Difference Between Dates",
    years: "years",
    months: "months",
    days: "days",
    weeks: "weeks",
    firstDate: "First Date",
    secondDate: "Second Date",
    correspondingHijri: "Corresponding Hijri Date",
    correspondingGregorian: "Corresponding Gregorian Date",
    totalDays: "Total Days",
    totalWeeks: "Total Weeks",
    totalMonths: "Total Months",
    totalYears: "Total Years",
    exactDifference: "Exact Difference",
    footer: {
      accuracy: "All calculations are based on the Umm al-Qura Hijri calendar",
      calendar: "The Hijri calendar used is Umm al-Qura calendar",
      copyright: "Made with ❤️ in 🇸🇦"
    }
  }
};

// Calendar month definitions
const GREGORIAN_MONTHS_AR = Object.freeze([
  { value: "1", label: "يناير" }, { value: "2", label: "فبراير" }, { value: "3", label: "مارس" },
  { value: "4", label: "أبريل" }, { value: "5", label: "مايو" }, { value: "6", label: "يونيو" },
  { value: "7", label: "يوليو" }, { value: "8", label: "أغسطس" }, { value: "9", label: "سبتمبر" },
  { value: "10", label: "أكتوبر" }, { value: "11", label: "نوفمبر" }, { value: "12", label: "ديسمبر" }
]);

const GREGORIAN_MONTHS_EN = Object.freeze([
  { value: "1", label: "January" }, { value: "2", label: "February" }, { value: "3", label: "March" },
  { value: "4", label: "April" }, { value: "5", label: "May" }, { value: "6", label: "June" },
  { value: "7", label: "July" }, { value: "8", label: "August" }, { value: "9", label: "September" },
  { value: "10", label: "October" }, { value: "11", label: "November" }, { value: "12", label: "December" }
]);

const HIJRI_MONTHS_AR = Object.freeze([
  { value: "1", label: "محرم" }, { value: "2", label: "صفر" }, { value: "3", label: "ربيع الأول" },
  { value: "4", label: "ربيع الثاني" }, { value: "5", label: "جمادى الأولى" }, { value: "6", label: "جمادى الآخرة" },
  { value: "7", label: "رجب" }, { value: "8", label: "شعبان" }, { value: "9", label: "رمضان" },
  { value: "10", label: "شوال" }, { value: "11", label: "ذو القعدة" }, { value: "12", label: "ذو الحجة" }
]);

const HIJRI_MONTHS_EN = Object.freeze([
  { value: "1", label: "Muharram" }, { value: "2", label: "Safar" }, { value: "3", label: "Rabi' al-Awwal" },
  { value: "4", label: "Rabi' al-Thani" }, { value: "5", label: "Jumada al-Awwal" }, { value: "6", label: "Jumada al-Thani" },
  { value: "7", label: "Rajab" }, { value: "8", label: "Sha'ban" }, { value: "9", label: "Ramadan" },
  { value: "10", label: "Shawwal" }, { value: "11", label: "Dhu al-Qadah" }, { value: "12", label: "Dhu al-Hijjah" }
]);

type ActionData = {
  errors?: {
    date: string;
  };
  originalInput?: {
    calendarType: 'gregorian' | 'hijri' | null;
    firstDate: {
      day: string;
      month: string;
      year: string;
    };
    secondDate: {
      day: string;
      month: string;
      year: string;
    };
  };
  difference?: {
    years: number;
    months: number;
    days: number;
    totalDays: number;
    totalWeeks: number;
    totalMonths: number;
    totalYears: number;
  };
  firstDate?: {
    gregorian: string;
    hijri: string;
  };
  secondDate?: {
    gregorian: string;
    hijri: string;
  };
};

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const calendarType = formData.get("calendarType") as 'gregorian' | 'hijri' | null;
  
  // First date
  const firstDay = formData.get("firstDay") as string;
  const firstMonth = formData.get("firstMonth") as string;
  const firstYear = formData.get("firstYear") as string;
  
  // Second date
  const secondDay = formData.get("secondDay") as string;
  const secondMonth = formData.get("secondMonth") as string;
  const secondYear = formData.get("secondYear") as string;

  if (!calendarType) {
    return json<ActionData>({ 
      errors: { date: "Please select a calendar system first." },
      originalInput: { 
        calendarType: null,
        firstDate: { day: firstDay, month: firstMonth, year: firstYear },
        secondDate: { day: secondDay, month: secondMonth, year: secondYear }
      }
    });
  }

  // Validate inputs
  if (!firstDay || !firstMonth || !firstYear || !secondDay || !secondMonth || !secondYear) {
    return json<ActionData>({
      errors: { date: "Please fill in all date fields." },
      originalInput: {
        calendarType,
        firstDate: { day: firstDay, month: firstMonth, year: firstYear },
        secondDate: { day: secondDay, month: secondMonth, year: secondYear }
      }
    });
  }

  try {
    let firstDate, secondDate;

    if (calendarType === 'gregorian') {
      firstDate = moment(`${firstYear}-${firstMonth}-${firstDay}`, 'YYYY-MM-DD');
      secondDate = moment(`${secondYear}-${secondMonth}-${secondDay}`, 'YYYY-MM-DD');
    } else {
      firstDate = moment(`${firstYear}-${firstMonth}-${firstDay}`, 'iYYYY-iM-iD');
      secondDate = moment(`${secondYear}-${secondMonth}-${secondDay}`, 'iYYYY-iM-iD');
    }

    if (!firstDate.isValid() || !secondDate.isValid()) {
      return json<ActionData>({
        errors: { date: "Invalid date entered. Please check your input." },
        originalInput: {
          calendarType,
          firstDate: { day: firstDay, month: firstMonth, year: firstYear },
          secondDate: { day: secondDay, month: secondMonth, year: secondYear }
        }
      });
    }

    // Calculate difference
    const diff = moment.duration(secondDate.diff(firstDate));
    const years = Math.abs(diff.years());
    const months = Math.abs(diff.months());
    const days = Math.abs(diff.days());

    // Get corresponding dates in both calendars
    const firstDateGregorian = calendarType === 'gregorian' 
      ? firstDate.format('YYYY-MM-DD')
      : firstDate.format('YYYY-MM-DD');
    const firstDateHijri = calendarType === 'gregorian'
      ? firstDate.format('iYYYY-iM-iD')
      : firstDate.format('iYYYY-iM-iD');

    const secondDateGregorian = calendarType === 'gregorian'
      ? secondDate.format('YYYY-MM-DD')
      : secondDate.format('YYYY-MM-DD');
    const secondDateHijri = calendarType === 'gregorian'
      ? secondDate.format('iYYYY-iM-iD')
      : secondDate.format('iYYYY-iM-iD');

    // Calculate total days between dates
    const totalDays = Math.abs(secondDate.diff(firstDate, 'days'));
    const totalWeeks = Math.floor(totalDays / 7);
    const totalMonths = years * 12 + months;
    const totalYears = years;

    return json<ActionData>({
      difference: {
        years,
        months,
        days,
        totalDays,
        totalWeeks,
        totalMonths,
        totalYears
      },
      firstDate: {
        gregorian: firstDateGregorian,
        hijri: firstDateHijri
      },
      secondDate: {
        gregorian: secondDateGregorian,
        hijri: secondDateHijri
      }
    });
  } catch (error) {
    return json<ActionData>({
      errors: { date: "An error occurred while calculating the date difference." },
      originalInput: {
        calendarType,
        firstDate: { day: firstDay, month: firstMonth, year: firstYear },
        secondDate: { day: secondDay, month: secondMonth, year: secondYear }
      }
    });
  }
}

export default function DateDifference() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  // Dark mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const storedMode = localStorage.getItem('darkMode');
      return storedMode !== null ? storedMode === 'true' : prefersDark;
    }
    return false;
  });

  // Language state
  const [lang, setLang] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('pageLang') || 'ar';
    }
    return 'ar';
  });
  const t = translations[lang as keyof typeof translations];

  // Calendar type state
  const [calendarType, setCalendarType] = useState<'gregorian' | 'hijri' | null>(null);

  // First date state
  const [firstDay, setFirstDay] = useState("");
  const [firstMonth, setFirstMonth] = useState("");
  const [firstYear, setFirstYear] = useState("");

  // Second date state
  const [secondDay, setSecondDay] = useState("");
  const [secondMonth, setSecondMonth] = useState("");
  const [secondYear, setSecondYear] = useState("");

  // Handle dark mode changes
  useEffect(() => {
    const updateDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    updateDarkMode();

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem('darkMode') === null) {
        if (e.matches) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        updateDarkMode();
      }
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          updateDarkMode();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  // Handle language changes
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    if (typeof window !== 'undefined') {
      localStorage.setItem('pageLang', lang);
    }
  }, [lang]);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    localStorage.setItem('darkMode', newMode.toString());
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const toggleLanguage = () => {
    const newLang = lang === 'ar' ? 'en' : 'ar';
    setLang(newLang);
  };

  const handleCalendarTypeChange = (newType: 'gregorian' | 'hijri') => {
    if (calendarType !== newType) {
      setCalendarType(newType);
      // Reset all date inputs
      setFirstDay("");
      setFirstMonth("");
      setFirstYear("");
      setSecondDay("");
      setSecondMonth("");
      setSecondYear("");
    }
  };

  const handleNumericInput = (e: React.ChangeEvent<HTMLInputElement>, setter: (value: string) => void) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setter(value);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/^\d$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
      e.preventDefault();
    }
  };

  const getButtonClasses = (type: 'gregorian' | 'hijri') => {
    const baseClasses = "px-6 py-3 rounded-lg font-semibold transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-sm";
    if (calendarType === type) {
      return `${baseClasses} bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 dark:from-purple-500 dark:to-indigo-500 dark:hover:from-purple-600 dark:hover:to-indigo-600 focus:ring-purple-500 dark:focus:ring-purple-400 dark:shadow-purple-500/20`;
    }
    return `${baseClasses} bg-slate-100/80 text-slate-700 hover:bg-slate-200/80 dark:bg-slate-800/80 dark:text-slate-200 dark:hover:bg-slate-700/80 focus:ring-slate-500 dark:focus:ring-slate-400 dark:shadow-slate-500/20 border border-white/20 dark:border-slate-600/50`;
  };

  const currentMonthOptions = calendarType === 'gregorian' 
    ? (lang === 'ar' ? GREGORIAN_MONTHS_AR : GREGORIAN_MONTHS_EN)
    : (calendarType === 'hijri' ? (lang === 'ar' ? HIJRI_MONTHS_AR : HIJRI_MONTHS_EN) : []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 py-4 sm:py-8 px-3 sm:px-4 flex flex-col items-center animate-fade-in transition-colors duration-300 relative overflow-hidden" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* Background Effects - تأثيرات خلفية */}
      <div className="absolute inset-0 pointer-events-none select-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-purple-200/30 rounded-full blur-2xl" />
        <div className="absolute bottom-20 right-20 w-24 h-24 bg-blue-200/30 rounded-full blur-2xl" />
        <div className="absolute top-1/2 left-1/3 w-20 h-20 bg-pink-200/20 rounded-full blur-2xl" />
      </div>
      
      <main className="bg-white/95 dark:bg-slate-800/95 p-4 sm:p-6 md:p-8 rounded-xl shadow-2xl w-full max-w-2xl relative animate-scale-in mx-2 sm:mx-4 backdrop-blur-sm transition-all duration-300 hover:shadow-purple-500/10 dark:hover:shadow-purple-500/5 border border-white/20 dark:border-slate-700/50 z-10">
        {/* Controls: Back, Language, and Dark mode toggle buttons */}
        <div className={`absolute top-2 sm:top-4 ${lang === 'ar' ? 'left-2 sm:left-4' : 'right-2 sm:right-4'} flex items-center gap-2 animate-fade-down`}>
          <Link
            to="/"
            className="p-1.5 sm:p-2 rounded-lg bg-slate-100/80 dark:bg-slate-700/80 hover:bg-slate-200/80 dark:hover:bg-slate-600/80 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 shadow-sm dark:shadow-slate-500/20 hover-lift backdrop-blur-sm border border-white/20 dark:border-slate-600/50"
            aria-label={lang === 'ar' ? "العودة إلى الصفحة الرئيسية" : "Back to Main Page"}
          >
            {lang === 'ar' ? (
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
            ) : (
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h17" /></svg>
            )}
          </Link>
          <button
            onClick={toggleLanguage}
            className="p-1.5 sm:p-2 rounded-lg bg-slate-100/80 dark:bg-slate-700/80 hover:bg-slate-200/80 dark:hover:bg-slate-600/80 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 shadow-sm dark:shadow-slate-500/20 hover-lift backdrop-blur-sm border border-white/20 dark:border-slate-600/50"
            aria-label={lang === 'ar' ? "Switch to English" : "التبديل إلى العربية"}
          >
            {lang === 'ar' ? 'EN' : 'عربي'}
          </button>
          <button
            onClick={toggleDarkMode}
            className="p-1.5 sm:p-2 rounded-lg bg-slate-100/80 dark:bg-slate-700/80 hover:bg-slate-200/80 dark:hover:bg-slate-600/80 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 shadow-sm dark:shadow-slate-500/20 hover-lift backdrop-blur-sm border border-white/20 dark:border-slate-600/50"
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? (
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700 dark:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
        </div>

        <div className="text-center mb-2 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-500 to-indigo-600 dark:from-purple-600 dark:to-indigo-700 rounded-full shadow-lg hover-lift transition-all duration-300">
            <span className="text-2xl sm:text-3xl" role="img" aria-label="Calendar Icon">📅</span>
          </div>
        </div>
        <header className="text-center mb-6 sm:mb-8 animate-fade-up">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 dark:text-white transition-colors duration-300">{t.title}</h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mt-2 transition-colors duration-300">{t.description}</p>
        </header>

        <Form method="post" className="space-y-4 sm:space-y-6 animate-fade-up">
          {calendarType && <input type="hidden" name="calendarType" value={calendarType} />}

          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-700 dark:text-slate-200 mb-2 sm:mb-3 transition-colors duration-300">{t.selectCalendar}</h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mb-4 sm:mb-6">
              <button 
                type="button" 
                onClick={() => handleCalendarTypeChange('gregorian')}
                className={`w-full sm:w-auto ${getButtonClasses('gregorian')} transition-all duration-300`}
                aria-pressed={calendarType === 'gregorian'}
              >
                <span role="img" aria-label="Gregorian Calendar" className={lang === 'ar' ? 'ml-2' : 'mr-2'}>☀️</span> {lang === 'ar' ? 'ميلادي' : 'Gregorian'}
              </button>
              <button 
                type="button" 
                onClick={() => handleCalendarTypeChange('hijri')}
                className={`w-full sm:w-auto ${getButtonClasses('hijri')} transition-all duration-300`}
                aria-pressed={calendarType === 'hijri'}
              >
                <span role="img" aria-label="Hijri Calendar" className={lang === 'ar' ? 'ml-2' : 'mr-2'}>🌙</span> {lang === 'ar' ? 'هجري' : 'Hijri'}
              </button>
            </div>
          </div>

          {/* First Date Input */}
          <div>
            <h2 className={`text-xl font-semibold text-slate-700 dark:text-slate-200 mb-3 transition-colors duration-300 ${!calendarType ? 'opacity-50' : ''}`}>{t.enterFirstDate}</h2>
            <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 ${!calendarType ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <div>
                <label htmlFor="firstDay" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 transition-colors duration-300">{t.day}</label>
                <input 
                  type="text" 
                  name="firstDay" 
                  id="firstDay" 
                  value={firstDay} 
                  onChange={(e) => handleNumericInput(e, setFirstDay)}
                  onKeyPress={handleKeyPress}
                  required 
                  min="1" 
                  max="31" 
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm disabled:bg-slate-100 dark:disabled:bg-slate-700/50 dark:bg-slate-700/50 dark:text-white transition-all duration-300" 
                  placeholder={lang === 'ar' ? "مثال: 25" : "e.g.: 25"} 
                  disabled={!calendarType}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
              <div>
                <label htmlFor="firstMonth" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 transition-colors duration-300">{t.month}</label>
                <select 
                  name="firstMonth" 
                  id="firstMonth" 
                  value={firstMonth} 
                  onChange={e => setFirstMonth(e.target.value)} 
                  required 
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm disabled:bg-slate-100 dark:disabled:bg-slate-700/50 dark:bg-slate-700/50 dark:text-white transition-all duration-300"
                  disabled={!calendarType}
                >
                  <option value="" disabled>
                    {!calendarType ? (lang === 'ar' ? "اختر التقويم أولاً" : "Select calendar first") : (lang === 'ar' ? "اختر الشهر" : "Select month")}
                  </option>
                  {currentMonthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="firstYear" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 transition-colors duration-300">{t.year}</label>
                <input 
                  type="text" 
                  name="firstYear" 
                  id="firstYear" 
                  value={firstYear} 
                  onChange={(e) => handleNumericInput(e, setFirstYear)}
                  onKeyPress={handleKeyPress}
                  required 
                  min="1000" 
                  max="3000"
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm disabled:bg-slate-100 dark:disabled:bg-slate-700/50 dark:bg-slate-700/50 dark:text-white transition-all duration-300" 
                  placeholder={calendarType === 'hijri' ? (lang === 'ar' ? "مثال: 1410" : "e.g.: 1410") : (lang === 'ar' ? "مثال: 1990" : "e.g.: 1990")}
                  disabled={!calendarType}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            </div>
          </div>

          {/* Second Date Input */}
          <div>
            <h2 className={`text-xl font-semibold text-slate-700 dark:text-slate-200 mb-3 transition-colors duration-300 ${!calendarType ? 'opacity-50' : ''}`}>{t.enterSecondDate}</h2>
            <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 ${!calendarType ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <div>
                <label htmlFor="secondDay" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 transition-colors duration-300">{t.day}</label>
                <input 
                  type="text" 
                  name="secondDay" 
                  id="secondDay" 
                  value={secondDay} 
                  onChange={(e) => handleNumericInput(e, setSecondDay)}
                  onKeyPress={handleKeyPress}
                  required 
                  min="1" 
                  max="31" 
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm disabled:bg-slate-100 dark:disabled:bg-slate-700/50 dark:bg-slate-700/50 dark:text-white transition-all duration-300" 
                  placeholder={lang === 'ar' ? "مثال: 25" : "e.g.: 25"} 
                  disabled={!calendarType}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
              <div>
                <label htmlFor="secondMonth" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 transition-colors duration-300">{t.month}</label>
                <select 
                  name="secondMonth" 
                  id="secondMonth" 
                  value={secondMonth} 
                  onChange={e => setSecondMonth(e.target.value)} 
                  required 
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm disabled:bg-slate-100 dark:disabled:bg-slate-700/50 dark:bg-slate-700/50 dark:text-white transition-all duration-300"
                  disabled={!calendarType}
                >
                  <option value="" disabled>
                    {!calendarType ? (lang === 'ar' ? "اختر التقويم أولاً" : "Select calendar first") : (lang === 'ar' ? "اختر الشهر" : "Select month")}
                  </option>
                  {currentMonthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="secondYear" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 transition-colors duration-300">{t.year}</label>
                <input 
                  type="text" 
                  name="secondYear" 
                  id="secondYear" 
                  value={secondYear} 
                  onChange={(e) => handleNumericInput(e, setSecondYear)}
                  onKeyPress={handleKeyPress}
                  required 
                  min="1000" 
                  max="3000"
                  className="mt-1 block w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm disabled:bg-slate-100 dark:disabled:bg-slate-700/50 dark:bg-slate-700/50 dark:text-white transition-all duration-300" 
                  placeholder={calendarType === 'hijri' ? (lang === 'ar' ? "مثال: 1410" : "e.g.: 1410") : (lang === 'ar' ? "مثال: 1990" : "e.g.: 1990")}
                  disabled={!calendarType}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            </div>
          </div>

          {actionData?.errors?.date && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-100/80 dark:bg-red-900/30 p-3 rounded-md border border-red-300/50 dark:border-red-700/50 animate-shake transition-all duration-300 backdrop-blur-sm" role="alert">
              {actionData.errors.date}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !calendarType}
            className="w-full flex justify-center py-2.5 sm:py-3 px-4 border border-transparent rounded-md shadow-sm text-base sm:text-lg font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 dark:from-purple-500 dark:to-indigo-500 dark:hover:from-purple-600 dark:hover:to-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:bg-slate-400 dark:disabled:bg-slate-600 transition-all duration-300 ease-in-out"
          >
            {isSubmitting ? t.calculating : t.calculate}
          </button>
        </Form>

        {actionData && !actionData.errors && actionData.difference && actionData.firstDate && actionData.secondDate && (
          <div className="mt-8 sm:mt-10 pt-4 sm:pt-6 border-t border-slate-200 dark:border-slate-700 animate-fade-up transition-all duration-300">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-800 dark:text-white mb-4 sm:mb-6 text-center transition-colors duration-300">{t.results}</h2>

            <div className="space-y-4 sm:space-y-6">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 p-4 rounded-lg border border-purple-200/50 dark:border-purple-800/50 shadow-lg hover-lift backdrop-blur-sm">
                <h3 className="text-lg font-semibold text-purple-700 dark:text-purple-400 mb-2">{t.difference}</h3>
                <div className="space-y-2">
                  <p className="text-base sm:text-lg text-slate-700 dark:text-slate-300">
                    {actionData.difference.years} {t.years}، و 
                    {actionData.difference.months} {t.months}، و 
                    {actionData.difference.days} {t.days}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                    <div className="bg-white/80 dark:bg-slate-800/80 p-2 rounded border border-purple-100/50 dark:border-purple-800/50 backdrop-blur-sm hover-lift transition-all duration-300 hover:shadow-md">
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.totalDays}</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{actionData.difference.totalDays}</p>
                    </div>
                    <div className="bg-white/80 dark:bg-slate-800/80 p-2 rounded border border-purple-100/50 dark:border-purple-800/50 backdrop-blur-sm hover-lift transition-all duration-300 hover:shadow-md">
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.totalWeeks}</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{actionData.difference.totalWeeks}</p>
                    </div>
                    <div className="bg-white/80 dark:bg-slate-800/80 p-2 rounded border border-purple-100/50 dark:border-purple-800/50 backdrop-blur-sm hover-lift transition-all duration-300 hover:shadow-md">
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.totalMonths}</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{actionData.difference.totalMonths}</p>
                    </div>
                    <div className="bg-white/80 dark:bg-slate-800/80 p-2 rounded border border-purple-100/50 dark:border-purple-800/50 backdrop-blur-sm hover-lift transition-all duration-300 hover:shadow-md">
                      <p className="text-xs text-slate-500 dark:text-slate-400">{t.totalYears}</p>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{actionData.difference.totalYears}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-50/80 dark:bg-slate-700/50 p-4 rounded-lg border border-slate-200/50 dark:border-slate-600/50 backdrop-blur-sm hover-lift transition-all duration-300 hover:shadow-md">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">{t.firstDate}</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    {calendarType === 'gregorian' ? actionData.firstDate.gregorian : actionData.firstDate.hijri}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {calendarType === 'gregorian' ? t.correspondingHijri : t.correspondingGregorian}: {calendarType === 'gregorian' ? actionData.firstDate.hijri : actionData.firstDate.gregorian}
                  </p>
                </div>

                <div className="bg-slate-50/80 dark:bg-slate-700/50 p-4 rounded-lg border border-slate-200/50 dark:border-slate-600/50 backdrop-blur-sm hover-lift transition-all duration-300 hover:shadow-md">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">{t.secondDate}</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    {calendarType === 'gregorian' ? actionData.secondDate.gregorian : actionData.secondDate.hijri}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {calendarType === 'gregorian' ? t.correspondingHijri : t.correspondingGregorian}: {calendarType === 'gregorian' ? actionData.secondDate.hijri : actionData.secondDate.gregorian}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="text-center mt-6 sm:mt-8 text-slate-600 dark:text-slate-400 text-xs sm:text-sm animate-fade-in px-4 transition-colors duration-300">
        <p>{t.footer.accuracy}</p>
        <p>{t.footer.calendar}</p>
        <p>
          <a 
            href="https://www.snapchat.com/@zk9c" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors duration-300"
          >
            {t.footer.copyright}
          </a>
        </p>
      </footer>
    </div>
  );
} 
